<template>
  <div id="signup_page">
    <div v-if="loadingSharedUser" style="margin: 5em auto; text-align: center">
      <img alt="Data loading" src="@/assets/img/snake_logo_animation_200.gif" />
      <p>Loading</p>
    </div>
    <SignUp v-else :shared_user="sharedUser" :redir_on_success="redirOnSuccess"></SignUp>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import SignUp from "@/components/SignUp.vue";
import { WSClient, ApiMessage } from "@/ems/WSClient";

export interface SharedUser {
  user_id: string;
  email_hash: string;
  Email: string;
  FirstName: string;
  has_password: boolean;
}

const props = defineProps<{
  user_id?: string;
  email_hash?: string;
}>();

const emit = defineEmits<{
  (e: "hide_login_bt", value: boolean): void;
}>();

const route = useRoute();
const loadingSharedUser = ref(false);
const sharedUser = ref<SharedUser | null>(null);
const redirOnSuccess = ref("");
const apiMessages = ref<ApiMessage[]>([]);
const client = new WSClient(loadingSharedUser, apiMessages);

const fetchSharedUser = async () => {
  const userId = props.user_id || route.params.user_id?.toString();
  const emailHash = props.email_hash || route.params.email_hash?.toString();

  if (userId && emailHash) {
    loadingSharedUser.value = true;
    try {
      const resp = await client.queryWs<{ user: { Email: string; FirstName: string; has_password: boolean } }>(
        "GET",
        "organization_user_userStatus",
        {
          user_id: userId,
          hash: emailHash,
        }
      );
      if (resp?.user) {
        sharedUser.value = {
          user_id: userId,
          email_hash: emailHash,
          Email: resp.user.Email,
          FirstName: resp.user.FirstName,
          has_password: resp.user.has_password,
        };
      }
    } catch (e) {
      console.error("Error fetching shared user", e);
    } finally {
      loadingSharedUser.value = false;
    }
  }
};

onMounted(async () => {
  emit("hide_login_bt", true);

  const searchParams = new URLSearchParams(document.location.search);
  redirOnSuccess.value = searchParams.get("redir") || route.query.redir?.toString() || "";

  if (redirOnSuccess.value) {
    try {
      const { default: router } = await import("@/router/index");
      let basePath = (router.options.history as any).base || "/v/";
      if (!basePath.endsWith("/")) {
        basePath += "/";
      }
      let pathToResolve = redirOnSuccess.value;
      if (pathToResolve.startsWith(basePath)) {
        pathToResolve = "/" + pathToResolve.substring(basePath.length);
      } else {
        const baseNoSlash = basePath.slice(0, -1);
        if (pathToResolve.startsWith(baseNoSlash + "/")) {
          pathToResolve = "/" + pathToResolve.substring(baseNoSlash.length + 1);
        }
      }
      if (!pathToResolve.startsWith("/")) {
        pathToResolve = "/" + pathToResolve;
      }
      const resolved = router.resolve(pathToResolve);
      if (resolved.name && resolved.name !== "NotFound") {
        const destination = {
          name: resolved.name,
          params: resolved.params,
          query: resolved.query,
        };
        sessionStorage.setItem("afterlogin", JSON.stringify(destination));
      }
    } catch (e) {
      console.error("Error saving redir to afterlogin", e);
    }
  }

  await fetchSharedUser();
});
</script>

<style scoped>
#signup_page {
  position: relative;
  text-align: center;
}
</style>
